import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import SendBird from 'sendbird';

type CallScreenData = { user: SendBird.User };

function useCallScreen() {
  const [callScreenData, setCallScreenData] = useState<CallScreenData | null>(
    null
  );

  const isCallVisible = !!callScreenData;
  const [callEventListenerMap] = useState(new Map());

  const addCallEventListener = useCallback(
    (id: string, listener: { onCallEnded?: (duration: number) => void }) => {
      callEventListenerMap.set(id, listener);
    },
    [callEventListenerMap]
  );

  const removeCallEventListener = useCallback(
    (id: string) => {
      callEventListenerMap.delete(id);
    },
    [callEventListenerMap]
  );

  const stopCall = useCallback(
    (duration: number) => {
      callEventListenerMap.forEach((listener) => {
        listener.onCallEnded?.(duration);
      });
      setCallScreenData(null);
    },
    [callEventListenerMap]
  );

  return useMemo(
    () => ({
      callScreenData,
      toggleCallScreen: setCallScreenData,
      isCallVisible,
      addCallEventListener,
      removeCallEventListener,
      stopCall,
    }),
    [
      addCallEventListener,
      callScreenData,
      isCallVisible,
      removeCallEventListener,
      stopCall,
    ]
  );
}

export const CallContext = createContext<{
  isCallVisible: boolean;
  callScreenData: CallScreenData | null;
  toggleCallScreen: (data: CallScreenData | null) => void;
  addCallEventListener: (
    id: string,
    listener: { onCallEnded?: (duration: number) => void }
  ) => void;
  removeCallEventListener: (id: string) => void;
  stopCall: (duration: number) => void;
}>({} as any);

export const useCallContext = () => useContext(CallContext);

export const CallContextProvider = ({ children }: { children: ReactNode }) => {
  const callContextValue = useCallScreen();
  return (
    <CallContext.Provider value={callContextValue}>
      {children}
    </CallContext.Provider>
  );
};
